// Persistence for attachments the operator has picked but not yet sent.
// Text drafts live in localStorage (composerDrafts.ts), but files
// can be up to 25 MB each, so they go to IndexedDB — the only browser store
// that takes a `File` verbatim, without a base64 round-trip through a 5 MB
// quota.
//
// Everything here is best-effort and swallows its own errors: a browser with
// IndexedDB blocked (private mode, storage pressure) must degrade to "no
// persistence", never to a composer that throws.
//
// The record is written when files are picked and cleared only once the message
// is accepted, so a failed or abandoned send always leaves the operator's
// attachment recoverable on reload.

import type { RcFile } from 'antd/es/upload'

const DB_NAME = 'chatwoot-composer'
const DB_VERSION = 1
const STORE = 'pendingAttachments'
// Drop records this old on open. Long enough that a send retried the next
// morning still finds its file, short enough that abandoned picks don't sit in
// the operator's browser forever.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

interface PendingRecord {
  key: string
  files: File[]
  savedAt: number
}

// The operator composer keys by conversation + mode so a reply draft and a
// private note never inherit each other's files; the new-conversation modal has
// a single composer and uses `newConversationKey`.
export function composerKey(conversationId: string, mode: string): string {
  return `${conversationId}:${mode}`
}

export const newConversationKey = 'new-conversation'

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      resolve(null)
      return
    }
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve(null)
    request.onblocked = () => resolve(null)
  })
}

// One transaction, resolved from the transaction (not the request) so the
// caller only continues once the write is durable.
async function withStore<T>(
  mode: 'readonly' | 'readwrite',
  run: (store: IDBObjectStore) => IDBRequest | null,
  readResult: (request: IDBRequest | null) => T
): Promise<T | null> {
  const db = await openDb()
  if (!db) {
    return null
  }
  try {
    return await new Promise<T | null>((resolve) => {
      const tx = db.transaction(STORE, mode)
      let request: IDBRequest | null = null
      try {
        request = run(tx.objectStore(STORE))
      } catch {
        resolve(null)
        return
      }
      tx.oncomplete = () => resolve(readResult(request))
      tx.onerror = () => resolve(null)
      tx.onabort = () => resolve(null)
    })
  } finally {
    db.close()
  }
}

// A structured clone keeps the File's bytes but drops the `uid` AntD assigns to
// an RcFile, which the composer uses as its React key and identity for removal.
// Re-stamp a fresh one on the way out.
function toRcFile(file: File): RcFile {
  return Object.assign(file, { uid: crypto.randomUUID() }) as RcFile
}

export async function savePendingAttachments(
  key: string,
  files: readonly RcFile[]
): Promise<void> {
  if (files.length === 0) {
    await clearPendingAttachments(key)
    return
  }
  const record: PendingRecord = {
    key,
    // Store plain Files: the RcFile extras don't survive structured cloning
    // anyway, and `toRcFile` restores what the UI needs.
    files: files.map(
      (file) => new File([file], file.name, { type: file.type })
    ),
    savedAt: Date.now(),
  }
  await withStore(
    'readwrite',
    (store) => store.put(record),
    () => undefined
  )
}

export async function loadPendingAttachments(key: string): Promise<RcFile[]> {
  const record = await withStore<PendingRecord | undefined>(
    'readonly',
    (store) => store.get(key),
    (request) => request?.result as PendingRecord | undefined
  )
  if (!record?.files?.length) {
    return []
  }
  if (Date.now() - record.savedAt > MAX_AGE_MS) {
    await clearPendingAttachments(key)
    return []
  }
  return record.files.map(toRcFile)
}

export async function clearPendingAttachments(key: string): Promise<void> {
  await withStore(
    'readwrite',
    (store) => store.delete(key),
    () => undefined
  )
}

// Re-key a pending record. Used when `start_conversation` creates the thread but
// its first message fails: the operator is dropped into the new conversation, so
// their unsent attachment has to follow them into that composer rather than stay
// behind under the new-conversation key.
export async function movePendingAttachments(
  fromKey: string,
  toKey: string
): Promise<void> {
  const files = await loadPendingAttachments(fromKey)
  if (files.length === 0) {
    return
  }
  await savePendingAttachments(toKey, files)
  await clearPendingAttachments(fromKey)
}

// Sweep records past MAX_AGE_MS. Called once when the inbox mounts so abandoned
// picks don't accumulate; failures are ignored like every other operation here.
export async function prunePendingAttachments(): Promise<void> {
  const db = await openDb()
  if (!db) {
    return
  }
  try {
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite')
      const cutoff = Date.now() - MAX_AGE_MS
      const cursorRequest = tx.objectStore(STORE).openCursor()
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result
        if (!cursor) {
          return
        }
        const record = cursor.value as PendingRecord
        if (!record?.savedAt || record.savedAt < cutoff) {
          cursor.delete()
        }
        cursor.continue()
      }
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
      tx.onabort = () => resolve()
    })
  } finally {
    db.close()
  }
}
